import type {
  AdminCreateExerciseInput,
  AdminCreateFoodInput,
  AdminExerciseItem,
  AdminFoodItem,
  AdminRange,
  AdminUserSummary,
} from '@onflow/shared';
import { useMemo, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import {
  useAdminAudit,
  useAdminCatalogMutations,
  useAdminExercises,
  useAdminFoods,
  useAdminOverview,
  useAdminUser,
  useAdminUsers,
} from '../hooks/useAdmin.js';

type AdminSection = 'overview' | 'users' | 'catalog' | 'audit';
type CatalogKind = 'foods' | 'exercises';

const NAV: Array<{ section: AdminSection; label: string; caption: string }> = [
  { section: 'overview', label: 'Overview', caption: 'Platform health' },
  { section: 'users', label: 'Users', caption: 'People and activity' },
  { section: 'catalog', label: 'Catalog', caption: 'Food and workouts' },
  { section: 'audit', label: 'Audit log', caption: 'Admin changes' },
];

export function Admin() {
  const location = useLocation();
  const tail = location.pathname.split('/')[2] as AdminSection | undefined;
  const section: AdminSection = NAV.some((item) => item.section === tail) ? tail! : 'overview';
  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <Link to="/admin" className="admin-brand">
          <span>OnFlow</span>
          <em>control</em>
        </Link>
        <nav aria-label="Admin sections">
          {NAV.map((item, index) => (
            <NavLink
              key={item.section}
              to={item.section === 'overview' ? '/admin' : `/admin/${item.section}`}
              end={item.section === 'overview'}
            >
              <span className="admin-nav-index">0{index + 1}</span>
              <span>
                <strong>{item.label}</strong>
                <small>{item.caption}</small>
              </span>
            </NavLink>
          ))}
        </nav>
        <Link to="/dashboard" className="admin-back">
          ← Back to OnFlow
        </Link>
      </aside>
      <main className="admin-main">
        {section === 'overview' && <Overview />}
        {section === 'users' && <Users />}
        {section === 'catalog' && <Catalog />}
        {section === 'audit' && <Audit />}
      </main>
    </div>
  );
}

function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="admin-page-header">
      <div>
        <span className="admin-eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action}
    </header>
  );
}

function Overview() {
  const [range, setRange] = useState<AdminRange>('30d');
  const { data, isLoading } = useAdminOverview(range);
  if (isLoading || !data) return <AdminLoading label="Reading platform activity" />;
  const cards = [
    ['Total users', data.metrics.totalUsers, 'all active accounts'],
    ['Active users', data.metrics.activeUsers, `in the last ${range}`],
    ['New users', data.metrics.newUsers, `joined in ${range}`],
    ['Onboarded', data.metrics.completedOnboarding, 'completed their profile'],
  ] as const;
  return (
    <>
      <PageHeader
        eyebrow="Control room"
        title="What is happening on OnFlow."
        description="A live operational view built from real product activity, not page-view vanity metrics."
        action={<RangePicker value={range} onChange={setRange} />}
      />
      <section className="admin-metric-grid" aria-label="Key metrics">
        {cards.map(([label, metric, note]) => (
          <article className="admin-metric-card" key={label}>
            <span>{label}</span>
            <strong>{metric.value.toLocaleString()}</strong>
            <footer>
              <Delta value={metric.changePercent} /> <small>{note}</small>
            </footer>
          </article>
        ))}
      </section>
      <div className="admin-two-column admin-two-column--wide">
        <section className="admin-panel">
          <PanelHead title="Daily product activity" note={`Last ${range}`} />
          <ActivityChart rows={data.activityTrend} />
        </section>
        <section className="admin-panel">
          <PanelHead title="Feature adoption" note="Unique users" />
          <div className="admin-adoption-list">
            {Object.entries({
              Workouts: data.engagement.workoutUsers,
              Nutrition: data.engagement.nutritionUsers,
              'Weight logs': data.engagement.weightLogUsers,
              Reviews: data.engagement.reviewUsers,
            }).map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <div>
                  <i
                    style={{
                      width: `${Math.min(100, (value / Math.max(1, data.metrics.activeUsers.value)) * 100)}%`,
                    }}
                  />
                </div>
                <strong>{value}</strong>
              </div>
            ))}
            <div className="admin-plan-count">
              <span>Active plans</span>
              <strong>{data.engagement.activePlans}</strong>
            </div>
          </div>
        </section>
      </div>
      <div className="admin-two-column">
        <section className="admin-panel">
          <PanelHead title="Recent activity" note="Latest 20" />
          <ActivityFeed items={data.recentActivity} />
        </section>
        <section className="admin-panel">
          <PanelHead title="Catalog coverage" note="Live inventory" />
          <div className="admin-catalog-summary">
            <CatalogCount
              value={data.catalogs.foods}
              label="active foods"
              archived={data.catalogs.archivedFoods}
            />
            <CatalogCount
              value={data.catalogs.exercises}
              label="active exercises"
              archived={data.catalogs.archivedExercises}
            />
          </div>
          <Link className="admin-text-link" to="/admin/catalog">
            Manage catalog →
          </Link>
        </section>
      </div>
    </>
  );
}

function RangePicker({
  value,
  onChange,
}: {
  value: AdminRange;
  onChange: (value: AdminRange) => void;
}) {
  return (
    <div className="admin-segmented" aria-label="Date range">
      {(['7d', '30d', '90d'] as const).map((item) => (
        <button
          key={item}
          className={value === item ? 'active' : ''}
          onClick={() => onChange(item)}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

function Delta({ value }: { value: number | null }) {
  if (value === null) return <b className="admin-delta neutral">new</b>;
  return (
    <b className={`admin-delta ${value > 0 ? 'up' : value < 0 ? 'down' : 'neutral'}`}>
      {value > 0 ? '↑' : value < 0 ? '↓' : '-'} {Math.abs(value)}%
    </b>
  );
}

function ActivityChart({
  rows,
}: {
  rows: NonNullable<ReturnType<typeof useAdminOverview>['data']>['activityTrend'];
}) {
  const max = Math.max(1, ...rows.map((r) => r.workouts + r.foodLogs + r.weightLogs + r.signups));
  return (
    <div className="admin-chart">
      <div className="admin-chart-bars">
        {rows.map((row, index) => {
          const total = row.workouts + row.foodLogs + row.weightLogs + row.signups;
          return (
            <div className="admin-chart-day" key={row.date} title={`${row.date}: ${total} events`}>
              <div
                className="admin-chart-stack"
                style={{ height: `${Math.max(3, (total / max) * 100)}%` }}
              >
                <i style={{ flex: row.foodLogs }} />
                <i style={{ flex: row.workouts }} />
                <i style={{ flex: row.weightLogs }} />
                <i style={{ flex: row.signups }} />
              </div>
              {(index === 0 ||
                index === rows.length - 1 ||
                index % Math.ceil(rows.length / 5) === 0) && <small>{row.date.slice(5)}</small>}
            </div>
          );
        })}
      </div>
      <div className="admin-chart-legend">
        <span>
          <i /> Food logs
        </span>
        <span>
          <i /> Workouts
        </span>
        <span>
          <i /> Weight
        </span>
        <span>
          <i /> Signups
        </span>
      </div>
    </div>
  );
}

function ActivityFeed({
  items,
}: {
  items: NonNullable<ReturnType<typeof useAdminOverview>['data']>['recentActivity'];
}) {
  return (
    <div className="admin-feed">
      {items.length === 0 ? (
        <Empty text="No activity in this period." />
      ) : (
        items.map((item) => (
          <div key={item.id}>
            <i className={`activity-dot ${item.kind}`} />
            <div>
              <strong>{item.userName ?? item.userEmail}</strong>
              <span>{item.detail}</span>
            </div>
            <time>{relativeTime(item.occurredAt)}</time>
          </div>
        ))
      )}
    </div>
  );
}

function CatalogCount({
  value,
  label,
  archived,
}: {
  value: number;
  label: string;
  archived: number;
}) {
  return (
    <div>
      <strong>{value}</strong>
      <span>{label}</span>
      <small>{archived} archived</small>
    </div>
  );
}

function Users() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive' | 'onboarded'>('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  const query = useMemo(
    () => ({ q: q || undefined, status, page, limit: 25 as const }),
    [q, status, page],
  );
  const { data, isLoading } = useAdminUsers(query);
  return (
    <>
      <PageHeader
        eyebrow="People"
        title="Know who is using the product."
        description="Search users, see where they are in the journey, and inspect meaningful activity without exposing credentials."
      />
      <div className="admin-toolbar">
        <label className="admin-search">
          <span>⌕</span>
          <input
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              setPage(1);
            }}
            placeholder="Search name or email"
          />
        </label>
        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as typeof status);
            setPage(1);
          }}
        >
          <option value="all">All users</option>
          <option value="active">Active · 30d</option>
          <option value="inactive">Inactive · 30d</option>
          <option value="onboarded">Onboarded</option>
        </select>
      </div>
      <section className="admin-panel admin-table-panel">
        {isLoading ? (
          <AdminLoading label="Loading users" />
        ) : (
          <UserTable items={data?.items ?? []} onSelect={setSelected} />
        )}
      </section>
      {data && <Pagination page={page} total={data.total} limit={data.limit} onChange={setPage} />}
      {selected && <UserDrawer id={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function UserTable({
  items,
  onSelect,
}: {
  items: AdminUserSummary[];
  onSelect: (id: string) => void;
}) {
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Joined</th>
            <th>Last active</th>
            <th>Mode</th>
            <th>Activity</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((user) => (
            <tr key={user.id} onClick={() => onSelect(user.id)}>
              <td>
                <div className="admin-person">
                  <Avatar user={user} />
                  <span>
                    <strong>{user.name ?? 'Unnamed user'}</strong>
                    <small>{user.email}</small>
                  </span>
                  {user.role === 'ADMIN' && <em>admin</em>}
                </div>
              </td>
              <td>{formatDate(user.createdAt)}</td>
              <td>{user.lastActiveAt ? relativeTime(user.lastActiveAt) : 'Never'}</td>
              <td>{user.trackingModes.join(' + ') || 'Not set'}</td>
              <td>
                <span className="admin-mono">
                  {user.counts.workouts}W · {user.counts.foodLogs}F
                </span>
              </td>
              <td>→</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length === 0 && <Empty text="No users match this view." />}
    </div>
  );
}

function UserDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useAdminUser(id);
  return (
    <div className="admin-drawer-backdrop">
      <aside className="admin-drawer" aria-label="User details">
        <button className="admin-close" onClick={onClose}>
          ×
        </button>
        {isLoading || !data ? (
          <AdminLoading label="Loading user" />
        ) : (
          <>
            <div className="admin-drawer-person">
              <Avatar user={data} />
              <span>
                <h2>{data.name ?? 'Unnamed user'}</h2>
                <p>{data.email}</p>
              </span>
            </div>
            <div className="admin-user-status">
              <span className={data.onboardingComplete ? 'good' : 'warn'}>
                {data.onboardingComplete ? 'Onboarded' : 'Setup incomplete'}
              </span>
              <span>{data.trackingModes.join(' + ') || 'No mode'}</span>
            </div>
            <h3>Goal</h3>
            <dl className="admin-detail-grid">
              <div>
                <dt>Type</dt>
                <dd>{humanize(data.goal.type)}</dd>
              </div>
              <div>
                <dt>Current</dt>
                <dd>{data.goal.currentWeightKg ? `${data.goal.currentWeightKg} kg` : '-'}</dd>
              </div>
              <div>
                <dt>Target</dt>
                <dd>{data.goal.targetWeightKg ? `${data.goal.targetWeightKg} kg` : '-'}</dd>
              </div>
              <div>
                <dt>Date</dt>
                <dd>{data.goal.targetDate ? formatDate(data.goal.targetDate) : '-'}</dd>
              </div>
            </dl>
            <h3>Product activity</h3>
            <dl className="admin-detail-grid">
              <div>
                <dt>Workouts</dt>
                <dd>{data.counts.workouts}</dd>
              </div>
              <div>
                <dt>Food logs</dt>
                <dd>{data.counts.foodLogs}</dd>
              </div>
              <div>
                <dt>Weights</dt>
                <dd>{data.counts.weightLogs}</dd>
              </div>
              <div>
                <dt>Reviews</dt>
                <dd>{data.counts.reviews}</dd>
              </div>
            </dl>
            <h3>Active plans</h3>
            {data.activePlans.length ? (
              data.activePlans.map((plan) => (
                <div className="admin-plan-row" key={plan.id}>
                  <span>{plan.type}</span>
                  <small>since {formatDate(plan.startsOn)}</small>
                </div>
              ))
            ) : (
              <p className="admin-muted">No active plans.</p>
            )}
          </>
        )}
      </aside>
    </div>
  );
}

function Catalog() {
  const [kind, setKind] = useState<CatalogKind>('foods');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'active' | 'archived' | 'all'>('active');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<AdminFoodItem | AdminExerciseItem | 'new' | null>(null);
  const query = useMemo(
    () => ({ q: q || undefined, status, page, limit: 25 as const }),
    [q, status, page],
  );
  const foods = useAdminFoods(query);
  const exercises = useAdminExercises(query);
  const result = kind === 'foods' ? foods : exercises;
  const mutations = useAdminCatalogMutations();
  const rows = result.data?.items ?? [];
  const pending = mutations.statusFood.isPending || mutations.statusExercise.isPending;
  function toggle(item: AdminFoodItem | AdminExerciseItem) {
    if (pending) return;
    const payload = { id: item.id, active: !item.active };
    if (kind === 'foods') mutations.statusFood.mutate(payload);
    else mutations.statusExercise.mutate(payload);
  }
  return (
    <>
      <PageHeader
        eyebrow="No-code catalog"
        title="The product library lives here."
        description="Add, correct, archive, and restore foods or exercises. Stable IDs protect plans and historical logs."
      />
      <section className="admin-catalog-create" aria-label="Create catalog item">
        <div>
          <span className="admin-eyebrow">Create catalog item</span>
          <strong>Add foods and exercises without changing code.</strong>
          <small>Choose what you want to add. The editor will open with all required fields.</small>
        </div>
        <div className="admin-catalog-create-actions">
          <button
            className={kind === 'foods' ? 'admin-primary' : 'admin-secondary'}
            onClick={() => {
              setKind('foods');
              setEditing('new');
            }}
          >
            + New food
          </button>
          <button
            className={kind === 'exercises' ? 'admin-primary' : 'admin-secondary'}
            onClick={() => {
              setKind('exercises');
              setEditing('new');
            }}
          >
            + New exercise
          </button>
        </div>
      </section>
      <div className="admin-catalog-tabs">
        <button
          className={kind === 'foods' ? 'active' : ''}
          onClick={() => {
            setKind('foods');
            setPage(1);
          }}
        >
          Foods <span>{foods.data?.total ?? '-'}</span>
        </button>
        <button
          className={kind === 'exercises' ? 'active' : ''}
          onClick={() => {
            setKind('exercises');
            setPage(1);
          }}
        >
          Exercises <span>{exercises.data?.total ?? '-'}</span>
        </button>
      </div>
      <div className="admin-toolbar">
        <label className="admin-search">
          <span>⌕</span>
          <input
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              setPage(1);
            }}
            placeholder={`Search ${kind}`}
          />
        </label>
        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as typeof status);
            setPage(1);
          }}
        >
          <option value="active">Active</option>
          <option value="archived">Archived</option>
          <option value="all">All records</option>
        </select>
      </div>
      <section className="admin-panel admin-table-panel">
        {result.isLoading ? (
          <AdminLoading label={`Loading ${kind}`} />
        ) : (
          <CatalogTable kind={kind} items={rows} onEdit={setEditing} onToggle={toggle} />
        )}
      </section>
      {result.data && (
        <Pagination
          page={page}
          total={result.data.total}
          limit={result.data.limit}
          onChange={setPage}
        />
      )}
      {editing && (
        <CatalogEditor
          kind={kind}
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function CatalogTable({
  kind,
  items,
  onEdit,
  onToggle,
}: {
  kind: CatalogKind;
  items: Array<AdminFoodItem | AdminExerciseItem>;
  onEdit: (item: AdminFoodItem | AdminExerciseItem) => void;
  onToggle: (item: AdminFoodItem | AdminExerciseItem) => void;
}) {
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Name / stable ID</th>
            {kind === 'foods' ? (
              <>
                <th>Category</th>
                <th>Macros / 100g</th>
                <th>Serving</th>
                <th>Source</th>
              </>
            ) : (
              <>
                <th>Muscle</th>
                <th>Equipment</th>
                <th>Type</th>
                <th>Steps</th>
              </>
            )}
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((raw) => {
            const item = raw;
            return (
              <tr key={item.id}>
                <td>
                  <strong>{item.name}</strong>
                  <small className="admin-id">{item.id}</small>
                </td>
                {isFoodItem(item) ? (
                  <>
                    <td>{humanize(item.category)}</td>
                    <td>
                      <span className="admin-mono">
                        {item.kcalPer100g} kcal · {item.proteinPer100g}P
                      </span>
                    </td>
                    <td>{item.defaultServingLabel}</td>
                    <td>{humanize(item.sourceRef)}</td>
                  </>
                ) : (
                  <>
                    <td>
                      {humanize(item.primaryMuscle)}
                      <small>{humanize(item.subMuscleGroup)}</small>
                    </td>
                    <td>{humanize(item.equipment)}</td>
                    <td>{humanize(item.type)}</td>
                    <td>{item.instructions.length}</td>
                  </>
                )}
                <td>
                  <span className={`admin-status ${item.active ? 'live' : 'archived'}`}>
                    {item.active ? 'Live' : 'Archived'}
                  </span>
                </td>
                <td>
                  <div className="admin-row-actions">
                    <button onClick={() => onEdit(raw)}>Edit</button>
                    <button onClick={() => onToggle(raw)}>
                      {item.active ? 'Archive' : 'Restore'}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {items.length === 0 && <Empty text="No catalog records match this view." />}
    </div>
  );
}

function CatalogEditor({
  kind,
  item,
  onClose,
}: {
  kind: CatalogKind;
  item: AdminFoodItem | AdminExerciseItem | null;
  onClose: () => void;
}) {
  const mutations = useAdminCatalogMutations();
  const mutation =
    kind === 'foods'
      ? item
        ? mutations.updateFood
        : mutations.createFood
      : item
        ? mutations.updateExercise
        : mutations.createExercise;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      if (kind === 'foods') {
        const input = foodInput(form, item as AdminFoodItem | null);
        if (item) await mutations.updateFood.mutateAsync({ id: item.id, input });
        else await mutations.createFood.mutateAsync(input as AdminCreateFoodInput);
      } else {
        const input = exerciseInput(form, item as AdminExerciseItem | null);
        if (item) await mutations.updateExercise.mutateAsync({ id: item.id, input });
        else await mutations.createExercise.mutateAsync(input as AdminCreateExerciseInput);
      }
      onClose();
    } catch {
      /* Global API toast keeps the error visible and the form open. */
    }
  }
  return (
    <div className="admin-modal-backdrop">
      <form className="admin-editor" onSubmit={submit}>
        <header>
          <div>
            <span className="admin-eyebrow">{item ? 'Edit record' : 'New record'}</span>
            <h2>{item?.name ?? `Add ${kind === 'foods' ? 'food' : 'exercise'}`}</h2>
          </div>
          <button type="button" className="admin-close" onClick={onClose}>
            ×
          </button>
        </header>
        {kind === 'foods' ? (
          <FoodFields item={item as AdminFoodItem | null} />
        ) : (
          <ExerciseFields item={item as AdminExerciseItem | null} />
        )}
        <footer>
          <button type="button" className="admin-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="admin-primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save to catalog'}
          </button>
        </footer>
      </form>
    </div>
  );
}

function Field({
  label,
  name,
  value,
  type = 'text',
  required = true,
  hint,
}: {
  label: string;
  name: string;
  value?: string | number | null;
  type?: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <label className="admin-field">
      <span>{label}</span>
      <input
        name={name}
        defaultValue={value ?? ''}
        type={type}
        required={required}
        step={type === 'number' ? 'any' : undefined}
      />
      {hint && <small>{hint}</small>}
    </label>
  );
}
function SelectField({
  label,
  name,
  value,
  options,
}: {
  label: string;
  name: string;
  value?: string | null;
  options: readonly string[];
}) {
  return (
    <label className="admin-field">
      <span>{label}</span>
      <select name={name} defaultValue={value ?? options[0]}>
        {options.map((option) => (
          <option key={option} value={option}>
            {humanize(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function FoodFields({ item }: { item: AdminFoodItem | null }) {
  return (
    <div className="admin-form-grid">
      <Field
        label="Stable ID"
        name="id"
        value={item?.id}
        required
        hint={item ? 'IDs cannot change after creation.' : 'lowercase_slug'}
      />
      <Field label="Display name" name="name" value={item?.name} />
      <SelectField
        label="Category"
        name="category"
        value={item?.category}
        options={[
          'protein',
          'grain',
          'vegetable',
          'fruit',
          'dairy',
          'fat_oil',
          'nut_seed',
          'legume',
          'beverage',
          'sweet',
          'snack',
          'supplement',
          'other',
        ]}
      />
      <SelectField
        label="Diet type"
        name="type"
        value={item?.type}
        options={['vegetarian', 'non_vegetarian', 'pescetarian', 'egg', 'vegan']}
      />
      <Field label="Kcal / 100g" name="kcalPer100g" value={item?.kcalPer100g} type="number" />
      <Field
        label="Protein / 100g"
        name="proteinPer100g"
        value={item?.proteinPer100g}
        type="number"
      />
      <Field label="Carbs / 100g" name="carbsPer100g" value={item?.carbsPer100g} type="number" />
      <Field label="Fat / 100g" name="fatPer100g" value={item?.fatPer100g} type="number" />
      <Field
        label="Default serving (g)"
        name="defaultServingG"
        value={item?.defaultServingG}
        type="number"
      />
      <Field label="Serving label" name="defaultServingLabel" value={item?.defaultServingLabel} />
      <SelectField
        label="Unit mode"
        name="unitType"
        value={item?.unitType}
        options={['grams', 'count', 'portion']}
      />
      <Field
        label="Grams per unit"
        name="defaultGramsPerUnit"
        value={item?.defaultGramsPerUnit}
        type="number"
        required={false}
      />
      <Field
        label="Small size (g)"
        name="sizeSmall"
        value={item?.sizes?.small}
        type="number"
        required={false}
      />
      <Field
        label="Medium size (g)"
        name="sizeMedium"
        value={item?.sizes?.medium}
        type="number"
        required={false}
      />
      <Field
        label="Large size (g)"
        name="sizeLarge"
        value={item?.sizes?.large}
        type="number"
        required={false}
      />
      <SelectField
        label="Source"
        name="sourceRef"
        value={item?.sourceRef}
        options={['ifct_2017', 'usda_fdc', 'recipe_derived', 'manufacturer']}
      />
      <Field label="Source code" name="sourceCode" value={item?.sourceCode} required={false} />
    </div>
  );
}

function ExerciseFields({ item }: { item: AdminExerciseItem | null }) {
  return (
    <div className="admin-form-grid">
      <Field
        label="Stable ID"
        name="id"
        value={item?.id}
        hint={item ? 'IDs cannot change after creation.' : 'lowercase_slug'}
      />
      <Field label="Display name" name="name" value={item?.name} />
      <SelectField
        label="Primary muscle"
        name="primaryMuscle"
        value={item?.primaryMuscle}
        options={['chest', 'back', 'shoulders', 'arms', 'legs', 'core', 'cardio', 'other']}
      />
      <Field
        label="Secondary muscles"
        name="secondaryMuscles"
        value={item?.secondaryMuscles.join(', ')}
        required={false}
        hint="Comma-separated"
      />
      <SelectField
        label="Equipment"
        name="equipment"
        value={item?.equipment}
        options={['barbell', 'dumbbell', 'machine', 'cable', 'bodyweight', 'other']}
      />
      <SelectField
        label="Tracking type"
        name="type"
        value={item?.type}
        options={['weight_reps', 'bodyweight_reps', 'duration', 'cardio']}
      />
      <SelectField
        label="Category"
        name="category"
        value={item?.category}
        options={['compound', 'isolation', 'cardio']}
      />
      <SelectField
        label="Sub-muscle"
        name="subMuscleGroup"
        value={item?.subMuscleGroup}
        options={[
          'upper_chest',
          'mid_chest',
          'lower_chest',
          'lats',
          'upper_back',
          'lower_back',
          'traps',
          'front_delts',
          'side_delts',
          'rear_delts',
          'short_head',
          'long_head',
          'brachialis',
          'long_head_triceps',
          'lateral_head',
          'medial_head',
          'forearms',
          'quads',
          'vastus_medialis',
          'hamstrings',
          'glutes',
          'gastrocnemius',
          'soleus',
          'adductors',
          'upper_abs',
          'lower_abs',
          'obliques',
          'cardio',
        ]}
      />
      <label className="admin-field admin-field--full">
        <span>Instructions</span>
        <textarea
          name="instructions"
          rows={7}
          defaultValue={item?.instructions.join('\n')}
          placeholder="One step per line"
          required
        />
      </label>
    </div>
  );
}

function foodInput(form: FormData, existing: AdminFoodItem | null) {
  const unitType = String(form.get('unitType')) as AdminFoodItem['unitType'];
  const small = optionalNumber(form.get('sizeSmall'));
  const medium = optionalNumber(form.get('sizeMedium'));
  const large = optionalNumber(form.get('sizeLarge'));
  return {
    ...(existing ? {} : { id: String(form.get('id')) }),
    name: String(form.get('name')),
    category: String(form.get('category')) as AdminFoodItem['category'],
    type: String(form.get('type')) as AdminFoodItem['type'],
    kcalPer100g: Number(form.get('kcalPer100g')),
    proteinPer100g: Number(form.get('proteinPer100g')),
    carbsPer100g: Number(form.get('carbsPer100g')),
    fatPer100g: Number(form.get('fatPer100g')),
    defaultServingG: Number(form.get('defaultServingG')),
    defaultServingLabel: String(form.get('defaultServingLabel')),
    unitType,
    sizes: unitType === 'portion' && small && medium && large ? { small, medium, large } : null,
    defaultGramsPerUnit:
      unitType === 'count' ? optionalNumber(form.get('defaultGramsPerUnit')) : null,
    sourceRef: String(form.get('sourceRef')) as AdminFoodItem['sourceRef'],
    sourceCode: String(form.get('sourceCode')).trim() || null,
  };
}
function exerciseInput(form: FormData, existing: AdminExerciseItem | null) {
  return {
    ...(existing ? {} : { id: String(form.get('id')) }),
    name: String(form.get('name')),
    primaryMuscle: String(form.get('primaryMuscle')) as AdminExerciseItem['primaryMuscle'],
    secondaryMuscles: String(form.get('secondaryMuscles'))
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean) as AdminExerciseItem['secondaryMuscles'],
    equipment: String(form.get('equipment')) as AdminExerciseItem['equipment'],
    type: String(form.get('type')) as AdminExerciseItem['type'],
    category: String(form.get('category')) as AdminExerciseItem['category'],
    subMuscleGroup: String(form.get('subMuscleGroup')) as AdminExerciseItem['subMuscleGroup'],
    instructions: String(form.get('instructions'))
      .split('\n')
      .map((v) => v.trim())
      .filter(Boolean),
  };
}

function Audit() {
  const { data, isLoading } = useAdminAudit();
  return (
    <>
      <PageHeader
        eyebrow="Accountability"
        title="Every privileged change, recorded."
        description="Catalog mutations are immutable audit events with the actor, target, action, and timestamp."
      />
      <section className="admin-panel">
        {isLoading ? (
          <AdminLoading label="Loading audit trail" />
        ) : (
          <div className="admin-audit-list">
            {data?.items.map((item) => (
              <div key={item.id}>
                <i className={item.action} />
                <span>
                  <strong>{item.actor.name ?? item.actor.email}</strong>
                  <small>
                    {item.action}d {item.entityType}
                  </small>
                </span>
                <code>{item.entityId}</code>
                <time>{formatDateTime(item.createdAt)}</time>
              </div>
            ))}
            {!data?.items.length && <Empty text="No admin changes yet." />}
          </div>
        )}
      </section>
    </>
  );
}

function Avatar({
  user,
}: {
  user: { name: string | null; email: string; avatarUrl: string | null };
}) {
  return user.avatarUrl ? (
    <img className="admin-avatar" src={user.avatarUrl} alt="" />
  ) : (
    <span className="admin-avatar admin-avatar--fallback">
      {(user.name ?? user.email).slice(0, 1).toUpperCase()}
    </span>
  );
}
function PanelHead({ title, note }: { title: string; note: string }) {
  return (
    <header className="admin-panel-head">
      <h2>{title}</h2>
      <span>{note}</span>
    </header>
  );
}
function Pagination({
  page,
  total,
  limit,
  onChange,
}: {
  page: number;
  total: number;
  limit: number;
  onChange: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="admin-pagination">
      <span>{total.toLocaleString()} records</span>
      <div>
        <button disabled={page <= 1} onClick={() => onChange(page - 1)}>
          ←
        </button>
        <span>
          {page} / {pages}
        </span>
        <button disabled={page >= pages} onClick={() => onChange(page + 1)}>
          →
        </button>
      </div>
    </div>
  );
}
function AdminLoading({ label }: { label: string }) {
  return (
    <div className="admin-loading">
      <i />
      <span>{label}</span>
    </div>
  );
}
function Empty({ text }: { text: string }) {
  return <div className="admin-empty">{text}</div>;
}
function humanize(value: string | null | undefined) {
  return value
    ? value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : '-';
}
function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
}
function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
function relativeTime(value: string) {
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 60) return 'just now';
  const unit = abs < 3600 ? 'minute' : abs < 86400 ? 'hour' : 'day';
  const divisor = unit === 'minute' ? 60 : unit === 'hour' ? 3600 : 86400;
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
    Math.round(seconds / divisor),
    unit,
  );
}
function optionalNumber(value: FormDataEntryValue | null) {
  const raw = String(value ?? '').trim();
  return raw ? Number(raw) : null;
}
function isFoodItem(item: AdminFoodItem | AdminExerciseItem): item is AdminFoodItem {
  return 'kcalPer100g' in item;
}
